import React, { useState, useMemo, useRef, useEffect } from "react";
import Modal from "../shared/Modal";
import "./AIReplay.css";
import { Button } from "../../ds2/components";
import { aireplayScenarios, AIReplayScenario } from "./aireplay_scenarios";
import { Eye } from "lucide-react";

type AIReplayProps = {
  isOpen: boolean;
  onClose: () => void;
  onClearDebugger: () => void;
  onRun: (scenarioNumber?: string) => void;
};

export const AIReplay = ({ isOpen, onClose, onClearDebugger, onRun }: AIReplayProps) => {
  const [filterText, setFilterText] = useState<string>("");
  const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(new Set());
  const [loadedScenarios, setLoadedScenarios] = useState<AIReplayScenario[]>([]);
  const [viewingScenario, setViewingScenario] = useState<AIReplayScenario | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Clear selected scenarios whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedScenarios(new Set());
      setViewingScenario(null);
    }
  }, [isOpen]);
  
  // Combine existing and loaded scenarios
  const allScenarios = useMemo(() => {
    const combined = [...aireplayScenarios, ...loadedScenarios];
    console.log("allScenarios updated:", {
      existing: aireplayScenarios.length,
      loaded: loadedScenarios.length,
      total: combined.length
    });
    return combined;
  }, [loadedScenarios]);
  
  // Filter scenarios based on input text
  const filteredScenarios = useMemo(() => {
    if (!filterText.trim()) {
      return allScenarios;
    }
    const filterLower = filterText.toLowerCase();
    return allScenarios.filter((scenario) =>
      scenario.name.toLowerCase().includes(filterLower) ||
      scenario.scenario.toLowerCase().includes(filterLower)
    );
  }, [filterText, allScenarios]);

  const selectedScenariosArray = Array.from(selectedScenarios);
  const selectedScenarioData = selectedScenariosArray.length === 1 
    ? allScenarios.find((scenario) => scenario.scenario === selectedScenariosArray[0])
    : null;

  const handleRun = () => {
    if (selectedScenariosArray.length !== 1) return;
    onRun(selectedScenariosArray[0]);
    onClose();
  };

  const handleRunAll = () => {
    onRun(undefined);
    onClose();
  };

  const handleClearDebugger = () => {
    setSelectedScenarios(new Set());
    onClearDebugger();
  };

  const handleCancel = () => {
    setFilterText("");
    setSelectedScenarios(new Set());
    onClose();
  };

  const handleScenarioClick = (scenarioId: string, e: React.MouseEvent) => {
    // Don't toggle selection if clicking on the eyeball icon
    if ((e.target as HTMLElement).closest('.scenario-view-icon')) {
      return;
    }
    
    setSelectedScenarios((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(scenarioId)) {
        newSet.delete(scenarioId);
      } else {
        newSet.add(scenarioId);
      }
      return newSet;
    });
  };

  const handleViewScenario = (scenario: AIReplayScenario, e: React.MouseEvent) => {
    e.stopPropagation();
    setViewingScenario(scenario);
  };

  const handleFileLoad = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        
        if (!Array.isArray(parsed)) {
          alert("Invalid file format: Expected an array of scenarios");
          return;
        }

        // Validate that each item has the required fields
        const validScenarios = parsed.filter((scenario: any, index: number) => {
          const isValid = (
            scenario !== null &&
            typeof scenario === "object" &&
            typeof scenario.name === "string" &&
            typeof scenario.scenario === "string" &&
            Array.isArray(scenario.steps) &&
            typeof scenario.attempts === "number"
          );
          
          if (!isValid) {
            console.warn(`Scenario at index ${index} is invalid:`, scenario);
          }
          
          return isValid;
        });

        if (validScenarios.length === 0) {
          alert("No valid scenarios found in file");
          return;
        }

        if (validScenarios.length < parsed.length) {
          alert(`Loaded ${validScenarios.length} valid scenarios (${parsed.length - validScenarios.length} invalid scenarios were skipped)`);
        } else {
          alert(`Successfully loaded ${validScenarios.length} scenario${validScenarios.length !== 1 ? "s" : ""}`);
        }

        console.log("Loading scenarios:", validScenarios);
        setLoadedScenarios((prev) => {
          const updated = [...prev, ...validScenarios];
          console.log("Updated loaded scenarios:", updated);
          return updated;
        });
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        alert(`Error parsing JSON file: ${error instanceof Error ? error.message : "Unknown error"}`);
        console.error("File load error:", error);
      }
    };

    reader.onerror = () => {
      alert("Error reading file");
    };

    reader.readAsText(file);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "800px", maxHeight: "90vh" }}
      canClose={true}
      escapeToClose={true}
      allowClickout={true}>
      <div className="ai-replay-container">
        <div className="ai-replay-header">
          <h1 className="ai-replay-title">AI Replay</h1>
        </div>

        <div className="ai-replay-content">
          <div className="ai-replay-form">
            <div className="ai-replay-form-group">
              {/* <label htmlFor="scenario-filter">Filter Scenarios:</label> */}
              <input
                id="scenario-filter"
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="ai-replay-input"
                placeholder="Type to filter scenarios by name or ID..."
              />
            </div>

            <div className="ai-replay-scenarios-list">
              <h4 className="ai-replay-scenarios-title">
                Available Scenarios {filteredScenarios.length !== allScenarios.length && `(${filteredScenarios.length} of ${allScenarios.length})`}
                {loadedScenarios.length > 0 && ` • ${loadedScenarios.length} loaded`}
                {selectedScenarios.size > 0 && ` • ${selectedScenarios.size} selected`}:
              </h4>
              {filteredScenarios.length === 0 ? (
                <div className="ai-replay-error">
                  No scenarios match "{filterText}"
                </div>
              ) : (
                <div className="ai-replay-scenarios-grid">
                  {filteredScenarios.map((scenario, index) => {
                    const isSelected = selectedScenarios.has(scenario.scenario);
                    return (
                      <div
                        key={`${scenario.scenario}-${index}`}
                        className={`ai-replay-scenario-item ${
                          isSelected ? "ai-replay-scenario-item-selected" : ""
                        }`}
                        onClick={(e) => handleScenarioClick(scenario.scenario, e)}>
                        <div className="ai-replay-scenario-item-content">
                          <div className="ai-replay-scenario-item-name">{scenario.name}</div>
                          <div className="ai-replay-scenario-item-id">{scenario.scenario}</div>
                          <div className="ai-replay-scenario-item-steps">{scenario.steps.length} step{scenario.steps.length !== 1 ? "s" : ""}</div>
                        </div>
                        <div
                          className="scenario-view-icon"
                          onClick={(e) => handleViewScenario(scenario, e)}
                          title="View JSON"
                        >
                          <Eye size={16} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="ai-replay-actions">
          <input
            ref={fileInputRef}
            id="scenario-file-input"
            type="file"
            accept=".json"
            onChange={handleFileLoad}
            style={{ display: "none" }}
          />
          <Button
            variant="secondary"
            onClick={handleClearDebugger}
            type="button"
            disabled={selectedScenarios.size === 0}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={handleRunAll}
            type="button">
            Run All
          </Button>
          <Button
            variant="primary"
            onClick={handleRun}
            type="button"
            disabled={selectedScenariosArray.length !== 1}>
            Run
          </Button>
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            type="button">
            Upload
          </Button>
          <Button
            variant="secondary"
            onClick={handleCancel}
            type="button">
            Cancel
          </Button>
        </div>
      </div>
      
      {/* Full-screen JSON view modal */}
      <Modal
        isOpen={viewingScenario !== null}
        onClose={() => setViewingScenario(null)}
        style={{
          width: "95vw",
          height: "95vh",
          maxWidth: "none",
          maxHeight: "none",
          padding: "0",
          display: "flex",
          flexDirection: "column",
        }}
        canClose={true}
        escapeToClose={true}
        allowClickout={true}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "#fff",
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #e9ecef",
          }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
              {viewingScenario?.name || "Scenario JSON"}
            </h2>
          </div>
          <div style={{
            flex: 1,
            overflow: "auto",
            padding: "20px",
            backgroundColor: "#f8f9fa",
          }}>
            <pre style={{
              margin: 0,
              padding: "16px",
              backgroundColor: "#fff",
              border: "1px solid #e9ecef",
              borderRadius: "4px",
              fontSize: "13px",
              lineHeight: "1.5",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            }}>
              {viewingScenario ? JSON.stringify(viewingScenario, null, 2) : ""}
            </pre>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};
